import axios from "axios";

// Wrapper axios dedicato alle chiamate RAG (proxy LF /api/rag/* -> container :4000).
// Inject del token JWT da localStorage in ogni request (interceptor).
// LF non ha un interceptor globale; questa istanza e' isolata al modulo RAG.

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";
const RAG_BASE = `${API_URL}/rag`;

const ragClient = axios.create({ baseURL: RAG_BASE });

ragClient.interceptors.request.use((config) => {
    const token = localStorage.getItem("token");
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// ---- bandi ----
export const bandiApi = {
    list: () => ragClient.get("/bandi").then((r) => r.data),
    get: (id) => ragClient.get(`/bandi/${id}`).then((r) => r.data),
    create: (data) => ragClient.post("/bandi", data).then((r) => r.data),
    update: (id, data) => ragClient.put(`/bandi/${id}`, data).then((r) => r.data),
    delete: (id) => ragClient.delete(`/bandi/${id}`).then((r) => r.data),
    listMembers: (id) => ragClient.get(`/bandi/${id}/members`).then((r) => r.data),
    inviteMember: (id, userId) =>
        ragClient.post(`/bandi/${id}/members`, { userId }).then((r) => r.data),
    removeMember: (id, userId) =>
        ragClient.delete(`/bandi/${id}/members/${userId}`).then((r) => r.data),
    listInvitableUsers: (id) =>
        ragClient.get(`/bandi/${id}/invitable-users`).then((r) => r.data),
};

// ---- conversations + messages (no streaming) ----
export const conversationsApi = {
    list: (bandoId) =>
        ragClient.get("/chat/conversations", { params: bandoId ? { bandoId } : {} }).then((r) => r.data),
    get: (id) => ragClient.get(`/chat/conversations/${id}`).then((r) => r.data),
    create: (data) => ragClient.post("/chat/conversations", data).then((r) => r.data),
    delete: (id) => ragClient.delete(`/chat/conversations/${id}`).then((r) => r.data),
};

// ---- documents ----
export const documentsApi = {
    list: (bandoId) =>
        ragClient.get("/documents", { params: bandoId ? { bandoId } : {} }).then((r) => r.data),
    get: (id) => ragClient.get(`/documents/${id}`).then((r) => r.data),
    fetchFile: (id) => ragClient.get(`/documents/${id}/file`, { responseType: "blob" }).then((r) => r.data),
    delete: (id) => ragClient.delete(`/documents/${id}`).then((r) => r.data),
    getVisibility: (id) => ragClient.get(`/documents/${id}/visibility`).then((r) => r.data),
    setVisibility: (id, payload) =>
        ragClient.put(`/documents/${id}/visibility`, payload).then((r) => r.data),
    upload: (file, options = {}) => {
        const formData = new FormData();
        formData.append("file", file);
        if (options.documentType) formData.append("documentType", options.documentType);
        if (options.bandoId) formData.append("bandoId", options.bandoId);
        return ragClient
            .post("/documents/upload", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            })
            .then((r) => r.data);
    },
};

// ---- streaming chat (SSE via fetch + ReadableStream) ----
// Async generator che emette eventi tipo { type: 'token'|'sources'|'cited_sources'|'done'|'error', data: ... }.
// Mantenuto fedele alla logica POC: parsing manuale di "data: <json>\n" lines.
export async function* streamChatMessage(conversationId, message) {
    const token = localStorage.getItem("token");
    const response = await fetch(`${RAG_BASE}/chat/conversations/${conversationId}/messages`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message }),
    });

    if (!response.ok) {
        const errText = await response.text().catch(() => "");
        throw new Error(`Stream request failed: ${response.status} ${errText}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() || "";
            for (const line of lines) {
                if (line.startsWith("data: ")) {
                    const jsonStr = line.slice(6);
                    try {
                        yield JSON.parse(jsonStr);
                    } catch {
                        // skip malformed line
                    }
                }
            }
        }
    } finally {
        reader.releaseLock();
    }
}
